import type { ReactNode } from 'react';
import styles from '../../styles/WeaponRack.module.css';
import clsx from '../../utils/clsx';
import type { RackFilter } from '../../engine/roster/rack';

interface RackToolbarProps {
  filter: RackFilter;
  onFilterChange: (next: RackFilter) => void;
  /** How many plates the filter is showing, out of how many there are. */
  shown: number;
  total: number;
  /** The Loadouts menu. */
  trailing?: ReactNode;
}

const TYPES: { key: 'ranged' | 'melee'; label: string }[] = [
  { key: 'ranged', label: 'Ranged' },
  { key: 'melee', label: 'Melee' },
];

/**
 * Search and type filters for a large armoury.
 *
 * A SEARCH FIELD RATHER THAN SUBTABS, deliberately. `weaponType` is
 * strictly 'ranged' | 'melee' - grenades are ranged weapons carrying
 * Blast(X) - so an All/Ranged/Melee/Other tab strip would need a category
 * invented into the data model, and fifteen bolt weapons would all still
 * live under one tab. A query has no such ceiling, and it is visible
 * state: a subtab is a mode you can forget you are in.
 *
 * Filtering is also why reorder switches off. "Move up" has no meaning in
 * a view that is not showing the whole order, and that order decides which
 * weapon parries - so the reason is stated on screen rather than left as a
 * control that quietly stops working.
 */
export default function RackToolbar({ filter, onFilterChange, shown, total, trailing }: RackToolbarProps) {
  const filtering = !!filter.query?.trim() || !!filter.type;

  return (
    <>
      <div className={styles.toolbar}>
        <input
          type="search"
          className={styles.search}
          data-testid="rack-search"
          aria-label="Search weapons"
          placeholder="Search name or rule…"
          value={filter.query ?? ''}
          onChange={(e) => onFilterChange({ ...filter, query: e.target.value })}
        />
        {TYPES.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={clsx(styles.chip, filter.type === key && styles.chipOn)}
            data-testid={`rack-filter-${key}`}
            aria-pressed={filter.type === key}
            onClick={() => onFilterChange({ ...filter, type: filter.type === key ? null : key })}
          >
            {/* The same emoji the plates wear - the drawn glyphs read as
                a megaphone and a bayonet, and were retired with them. */}
            <span aria-hidden="true">{key === 'melee' ? '\u2694' : '\uD83C\uDFAF'}</span>
            {label}
          </button>
        ))}
        {trailing}
      </div>
      {filtering && (
        <div className={styles.filterNote} role="status">
          Showing {shown} of {total}. Reordering is off while filtered.
          <button type="button" className={styles.clearFilter} onClick={() => onFilterChange({})}>
            Clear
          </button>
        </div>
      )}
    </>
  );
}
